/**
 * LeasingStammdaten — Leasing-Stammdaten (Phase L1a, Jun-2026).
 * Leasinggesellschaften (netto/brutto, Vertragsart) + Aufschlags-/Modell-
 * Lerntabelle (Hersteller + FIN-Präfix-10 → Aufschlag + Modell) + FIN-Decoder.
 */
import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Trash2, Loader2, ScanLine, BookOpen, Save, Upload, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { VinWarning } from "../../components/common/VinWarning";
import { BankImportCard } from "../../components/leasing/BankImportCard";

const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function LeasingStammdaten({ view = "stammdaten" }) {
    const [companies, setCompanies] = useState([]);
    const [busy, setBusy] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const c = await api.get("/dealer/leasing/companies");
            setCompanies(c.data.items || []);
        } catch { /* noop */ }
        finally { setBusy(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const bump = () => setReloadKey((k) => k + 1);

    if (view === "import") {
        return (
            <div className="space-y-8" data-testid="leasing-import-view">
                <BankImportCard onDone={() => { load(); bump(); }} />
                <CompaniesCard companies={companies} onChanged={load} busy={busy} />
                <LeasingCentersCard />
            </div>
        );
    }
    if (view === "all") {
        return (
            <div className="space-y-8" data-testid="leasing-stammdaten-all">
                <LeasingCentersCard />
                <VinDecoder onLearned={bump} />
                <ImportedVehiclesCard reloadKey={reloadKey} onChanged={bump} />
                <BankImportCard onDone={() => { load(); bump(); }} />
                <CompaniesCard companies={companies} onChanged={load} busy={busy} />
            </div>
        );
    }
    return (
        <div className="space-y-8" data-testid="leasing-stammdaten">
            <VinDecoder onLearned={bump} />
            <ImportedVehiclesCard reloadKey={reloadKey} onChanged={bump} />
        </div>
    );
}
const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function LeasingCentersCard() {
    const [centers, setCenters] = useState([]);
    const [avail, setAvail] = useState([]);
    const [busy, setBusy] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [c, a] = await Promise.all([
                api.get("/leasing-centers"),
                api.get("/leasing-centers/available-users"),
            ]);
            setCenters(c.data.centers || []);
            setAvail(a.data.users || []);
        } catch { /* noop */ } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    const addOne = async (uid) => {
        if (!uid) return;
        setSaving(true);
        try { await api.post("/leasing-centers", { user_ids: [uid] }); await load(); toast.success("Standort hinzugefügt"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Hinzufügen fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    const patch = async (id, upd) => {
        try { await api.patch(`/leasing-centers/${id}`, upd); setCenters((cs) => cs.map((c) => c.id === id ? { ...c, ...upd } : c)); }
        catch { toast.error("Speichern fehlgeschlagen"); }
    };
    const remove = async (id) => { try { await api.delete(`/leasing-centers/${id}`); await load(); } catch { /* noop */ } };

    const addable = avail.filter((u) => !u.already_added);

    return (
        <section className="border rounded-sm bg-white" data-testid="leasing-centers-card">
            <div className="px-4 py-3 border-b flex items-center gap-2">
                <Building2 className="w-4 h-4 text-indigo-600" />
                <h3 className="font-black text-sm text-[#111827]">Leasingcenter / Rückgabe-Standorte</h3>
            </div>
            <div className="p-4 space-y-4">
                <p className="text-[11px] text-gray-500">Wählen Sie registrierte Standorte als Rückgabeorte aus. Kunden buchen ihren Rückgabetermin online slot-basiert je Standort. Mehrfachauswahl möglich.</p>

                {addable.length > 0 && (
                    <div className="border rounded-sm p-3 bg-gray-50 space-y-2" data-testid="leasing-centers-add">
                        <div className="text-xs font-bold text-gray-600">Standort hinzufügen</div>
                        <Select onValueChange={addOne} disabled={saving} value="">
                            <SelectTrigger className="h-9 text-sm bg-white" data-testid="leasing-centers-select">
                                <SelectValue placeholder={saving ? "Wird hinzugefügt…" : "Registrierten Händler / Standort auswählen…"} />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {addable.map((u) => (
                                    <SelectItem key={u.user_id} value={u.user_id} data-testid={`leasing-center-opt-${u.user_id}`}>
                                        {u.name}{u.address ? ` — ${u.address}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[10px] text-gray-400">Standort auswählen, um ihn als Rückgabeort hinzuzufügen.</p>
                    </div>
                )}

                {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : centers.length === 0 ? (
                    <div className="text-sm text-gray-400" data-testid="leasing-centers-empty">Noch keine Leasingcenter festgelegt.</div>
                ) : (
                    <div className="space-y-3" data-testid="leasing-centers-list">
                        {centers.map((c) => (
                            <div key={c.id} className="border rounded-sm p-3 space-y-2" data-testid={`leasing-center-${c.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div><div className="font-bold text-sm">{c.name}</div><div className="text-[11px] text-gray-400">{c.address}</div></div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-[11px] inline-flex items-center gap-1"><input type="checkbox" checked={c.active !== false} onChange={(e) => patch(c.id, { active: e.target.checked })} data-testid={`leasing-center-active-${c.id}`} /> aktiv</label>
                                        <button onClick={() => remove(c.id)} className="text-gray-400 hover:text-red-600" data-testid={`leasing-center-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {WD.map((d, i) => {
                                        const on = (c.weekdays || []).includes(i);
                                        return <button key={i} onClick={() => { const w = new Set(c.weekdays || []); on ? w.delete(i) : w.add(i); patch(c.id, { weekdays: [...w].sort() }); }} className={`text-[11px] px-2 py-1 rounded-sm border ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-500"}`} data-testid={`leasing-center-wd-${c.id}-${i}`}>{d}</button>;
                                    })}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                    <label className="inline-flex items-center gap-1">Von <input type="time" defaultValue={c.start_time} onBlur={(e) => patch(c.id, { start_time: e.target.value })} className="border rounded-sm px-1 py-0.5" data-testid={`leasing-center-start-${c.id}`} /></label>
                                    <label className="inline-flex items-center gap-1">Bis <input type="time" defaultValue={c.end_time} onBlur={(e) => patch(c.id, { end_time: e.target.value })} className="border rounded-sm px-1 py-0.5" data-testid={`leasing-center-end-${c.id}`} /></label>
                                    <label className="inline-flex items-center gap-1">Slot (Min) <input type="number" min="15" step="15" defaultValue={c.slot_minutes} onBlur={(e) => patch(c.id, { slot_minutes: Number(e.target.value) })} className="border rounded-sm px-1 py-0.5 w-16" data-testid={`leasing-center-slot-${c.id}`} /></label>
                                    <label className="inline-flex items-center gap-1">Kapazität/Slot <input type="number" min="1" defaultValue={c.capacity} onBlur={(e) => patch(c.id, { capacity: Number(e.target.value) })} className="border rounded-sm px-1 py-0.5 w-14" data-testid={`leasing-center-cap-${c.id}`} /></label>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}



function ImportedVehiclesCard({ reloadKey, onChanged }) {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ total: 0, pages: 1 });
    const [q, setQ] = useState("");
    const [withS, setWithS] = useState("");      // "" | yes | no
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState(false);
    const [importing, setImporting] = useState(false);
    const [edits, setEdits] = useState({});      // prefix -> { manufacturer, model, surcharge }
    const [bulk, setBulk] = useState({ fuel: "", ccm: "", surcharge: "", manufacturer: "", model: "" });
    const [bulkBusy, setBulkBusy] = useState(false);
    const [selected, setSelected] = useState(() => new Set());  // angekreuzte FIN-Präfixe
    const toggleSel = (p) => setSelected((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });

    const AUFSCHLAG_OPTS = ["0.00", "225.25", "525.25", "725.25", "1225.25", "1425.25", "1625.25", "1825.25", "2025.25"];
    const CCM_OPTS = ["49", "124", "125", "278"];
    const fmtEur = (v) => Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const load = useCallback(async (p = 1) => {
        setBusy(true);
        try {
            const params = { page: p, page_size: pageSize };
            if (q.trim()) params.q = q.trim();
            if (withS) params.with_surcharge = withS;
            const { data } = await api.get("/dealer/leasing/imported-vehicles", { params });
            setItems(data.items || []);
            setMeta({ total: data.total ?? 0, pages: data.pages ?? 1 });
            setPage(data.page ?? p);
        } catch { /* noop */ }
        finally { setBusy(false); }
    }, [q, withS, pageSize]);
    useEffect(() => { const t = setTimeout(() => load(1), 300); return () => clearTimeout(t); }, [load]);
    useEffect(() => { if (reloadKey) load(1); /* eslint-disable-next-line */ }, [reloadKey]);

    const setEdit = (prefix, field, val) => setEdits((e) => ({ ...e, [prefix]: { ...(e[prefix] || {}), [field]: val } }));

    const onBulkImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/dealer/leasing/surcharge-rules/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Import: ${data.created} neu, ${data.updated} aktualisiert, ${data.skipped} übersprungen`);
            await load(page); onChanged && onChanged();
        } catch (e2) { toast.error(e2?.response?.data?.detail || "Import fehlgeschlagen"); }
        finally { setImporting(false); e.target.value = ""; }
    };

    const deleteRule = async (row) => {
        if (!row.rule_id) return;
        try {
            await api.delete(`/dealer/leasing/surcharge-rules/${row.rule_id}`);
            toast.success(`Aufschlag für ${row.vin_prefix} entfernt.`);
            await load(page); onChanged && onChanged();
        } catch { toast.error("Löschen fehlgeschlagen."); }
    };

    const saveSurcharge = async (row) => {
        const ed = edits[row.vin_prefix] || {};
        const manufacturer = (ed.manufacturer ?? row.manufacturer ?? "").trim();
        const model = (ed.model ?? row.model ?? "").trim();
        const fuel = (ed.fuel ?? row.fuel ?? "").trim();
        const rawD = ed.displacement ?? (row.displacement != null ? String(row.displacement) : "");
        const rawS = ed.surcharge ?? (row.surcharge != null ? String(row.surcharge) : "");
        const val = Number(rawS);
        if (!manufacturer) { toast.error("Bitte einen Hersteller angeben."); return; }
        if (rawS === "" || isNaN(val)) { toast.error("Bitte einen Aufschlag eingeben."); return; }
        try {
            await api.post("/dealer/leasing/surcharge-rules", {
                manufacturer, vin_prefix: row.vin_prefix, model: model || null, surcharge: val,
                fuel: fuel || null, displacement: rawD === "" ? null : Number(rawD),
            });
            toast.success(`Gespeichert für Präfix ${row.vin_prefix}${row.vin_count > 1 ? ` (gilt für ${row.vin_count} Fahrzeuge)` : ""}.`);
            setEdits((e) => { const c = { ...e }; delete c[row.vin_prefix]; return c; });
            await load(page); onChanged && onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    };

    const bulkSave = async () => {
        if (bulk.surcharge === "" || isNaN(Number(bulk.surcharge))) {
            toast.error("Bitte einen Aufschlag für die Sammelbearbeitung wählen."); return;
        }
        const hasSel = selected.size > 0;
        const scope = hasSel
            ? `NUR die ${selected.size} angekreuzten Präfixe`
            : `ALLE gefilterten Präfixe (${withS === "no" ? "ohne Aufschlag" : withS === "yes" ? "mit Aufschlag" : "alle"}${q.trim() ? `, Suche „${q.trim()}"` : ""})`;
        if (!window.confirm(`Aufschlag ${fmtEur(bulk.surcharge)} €${bulk.fuel ? ` · ${bulk.fuel}` : ""}${bulk.ccm ? ` · ${bulk.ccm} ccm` : ""} auf ${scope} anwenden?`)) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/dealer/leasing/surcharge-rules/bulk", {
                q: q.trim() || null, with_surcharge: withS || null,
                prefixes: hasSel ? [...selected] : null,
                fuel: bulk.fuel || null, displacement: bulk.ccm === "" ? null : Number(bulk.ccm),
                manufacturer: bulk.manufacturer || null, model: bulk.model || null,
                surcharge: Number(bulk.surcharge),
            });
            toast.success(`Sammelbearbeitung: ${data.updated} Präfixe gespeichert, ${data.recomputed_returns} Restwerte aktualisiert.`);
            setBulk({ fuel: "", ccm: "", surcharge: "", manufacturer: "", model: "" });
            setSelected(new Set());
            await load(page); onChanged && onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Sammelbearbeitung fehlgeschlagen."); }
        finally { setBulkBusy(false); }
    };

    return (
        <section className="border rounded-sm bg-white" data-testid="leasing-imported-vehicles-card">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-black text-sm text-[#111827]">Aufschlags-/Modell-Tabelle ({meta.total.toLocaleString("de-DE")})</h3>
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 rounded-sm px-3 py-1.5 cursor-pointer hover:border-indigo-400" data-testid="leasing-rules-import-label" title="Excel/CSV: Spalten Hersteller, FIN/Präfix, Modell, Aufschlag">
                    {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Massen-Import
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onBulkImport} data-testid="leasing-rules-import" />
                </label>
            </div>
            <div className="px-4 pt-2 text-[11px] text-gray-500">Alle FINs aus deinen Vorgängen, <strong>nach den ersten 10 Zeichen zusammengefasst</strong> (Dubletten nur einmal). Hersteller/Modell sind editierbar; der gespeicherte Aufschlag gilt für <strong>alle</strong> Fahrzeuge mit diesem 10-Zeichen-Präfix. Per <strong>Massen-Import</strong> (Excel/CSV: Hersteller, FIN/Präfix, Modell, Aufschlag) lassen sich Regeln auf einmal hinterlegen.</div>
            <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche FIN / Hersteller / Modell" className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm flex-1 min-w-[220px]" data-testid="leasing-iv-search" />
                    <select value={withS} onChange={(e) => setWithS(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-iv-filter">
                        <option value="">Alle</option>
                        <option value="no">Ohne Aufschlag</option>
                        <option value="yes">Mit Aufschlag</option>
                    </select>
                    <div className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <span className="font-bold uppercase tracking-wider text-[10px]">Pro Seite</span>
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-iv-page-size">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                        </select>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 items-end bg-indigo-50/50 border border-indigo-200 rounded-sm px-3 py-2.5" data-testid="leasing-iv-bulk-bar">
                    <div className="text-[11px] font-black uppercase tracking-wider text-indigo-700 w-full">Sammelbearbeitung — {selected.size > 0 ? <span className="text-emerald-700">gilt für {selected.size} ANGEKREUZTE Präfixe</span> : `gilt für ALLE gefilterten Präfixe (${meta.total.toLocaleString("de-DE")})`}</div>
                    <label className="text-[11px] text-gray-600">Hersteller
                        <input value={bulk.manufacturer} onChange={(e) => setBulk((b) => ({ ...b, manufacturer: e.target.value }))} placeholder="unverändert" className="block border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5 w-32" data-testid="leasing-iv-bulk-manufacturer" />
                    </label>
                    <label className="text-[11px] text-gray-600">Modell
                        <input value={bulk.model} onChange={(e) => setBulk((b) => ({ ...b, model: e.target.value }))} placeholder="unverändert" className="block border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5 w-40" data-testid="leasing-iv-bulk-model" />
                    </label>
                    <label className="text-[11px] text-gray-600">Kraftstoff
                        <select value={bulk.fuel} onChange={(e) => setBulk((b) => ({ ...b, fuel: e.target.value }))} className="block border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5" data-testid="leasing-iv-bulk-fuel">
                            <option value="">— unverändert —</option>
                            <option value="Benzin">Benzin</option>
                            <option value="Elektro">Elektro</option>
                        </select>
                    </label>
                    <label className="text-[11px] text-gray-600">ccm
                        <input list="leasing-ccm-opts" value={bulk.ccm} onChange={(e) => setBulk((b) => ({ ...b, ccm: e.target.value }))} placeholder="unverändert" className="block border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5 w-24 font-mono-num" data-testid="leasing-iv-bulk-ccm" />
                    </label>
                    <label className="text-[11px] text-gray-600">Aufschlag (€)
                        <select value={bulk.surcharge} onChange={(e) => setBulk((b) => ({ ...b, surcharge: e.target.value }))} className="block border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white mt-0.5" data-testid="leasing-iv-bulk-surcharge">
                            <option value="">— wählen —</option>
                            {AUFSCHLAG_OPTS.map((o) => <option key={o} value={o}>{fmtEur(o)} €</option>)}
                        </select>
                    </label>
                    <Button onClick={bulkSave} disabled={bulkBusy} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-xs font-bold" data-testid="leasing-iv-bulk-apply">
                        {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} {selected.size > 0 ? `Auf ${selected.size} ausgewählte anwenden` : "Auf alle anwenden"}
                    </Button>
                    {selected.size > 0 && (
                        <button onClick={() => setSelected(new Set())} className="text-[11px] font-bold text-gray-500 hover:text-gray-800 underline h-9" data-testid="leasing-iv-bulk-clearsel">Auswahl aufheben</button>
                    )}
                </div>
                <datalist id="leasing-ccm-opts">{CCM_OPTS.map((c) => <option key={c} value={c} />)}</datalist>
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : items.length === 0 ? (
                    <div className="text-sm text-gray-400 py-3" data-testid="leasing-iv-empty">Keine importierten Fahrzeuge gefunden.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="leasing-iv-table">
                            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b">
                                <th className="py-2 pr-2 w-8">
                                    <input type="checkbox" data-testid="leasing-iv-select-all"
                                        title="Alle auf dieser Seite an-/abwählen"
                                        checked={items.length > 0 && items.every((r) => selected.has(r.vin_prefix))}
                                        onChange={(e) => setSelected((s) => {
                                            const n = new Set(s);
                                            items.forEach((r) => e.target.checked ? n.add(r.vin_prefix) : n.delete(r.vin_prefix));
                                            return n;
                                        })} />
                                </th>
                                <th className="py-2 pr-3">FIN-Präfix (10)</th><th className="py-2 pr-3">Hersteller</th><th className="py-2 pr-3">Modell</th><th className="py-2 pr-3">Kraftstoff</th><th className="py-2 pr-3">ccm</th><th className="py-2 pr-3 text-right">Aufschlag (€)</th><th></th>
                            </tr></thead>
                            <tbody>
                                {items.map((row) => {
                                    const ed = edits[row.vin_prefix] || {};
                                    const manu = ed.manufacturer ?? row.manufacturer ?? "";
                                    const model = ed.model ?? row.model ?? "";
                                    const surch = ed.surcharge ?? (row.surcharge != null ? String(row.surcharge) : "");
                                    const fuel = ed.fuel ?? row.fuel ?? "";
                                    const ccm = ed.displacement ?? (row.displacement != null ? String(row.displacement) : "");
                                    return (
                                        <tr key={row.vin_prefix} className={`border-b last:border-0 align-top ${selected.has(row.vin_prefix) ? "bg-emerald-50/60" : ""}`} data-testid={`leasing-iv-row-${row.vin_prefix}`}>
                                            <td className="py-2 pr-2">
                                                <input type="checkbox" checked={selected.has(row.vin_prefix)} onChange={() => toggleSel(row.vin_prefix)} data-testid={`leasing-iv-select-${row.vin_prefix}`} />
                                            </td>
                                            <td className="py-2 pr-3 font-mono-num whitespace-nowrap">
                                                {row.vin_prefix}
                                                {row.surcharge != null && <span className="ml-1 text-[9px] uppercase font-bold text-emerald-600">hinterlegt</span>}
                                                {row.vin_count > 1 && <div className="text-[10px] text-gray-400 font-sans">gilt für {row.vin_count} Fahrzeuge</div>}
                                            </td>
                                            <td className="py-2 pr-3"><Input value={manu} onChange={(e) => setEdit(row.vin_prefix, "manufacturer", e.target.value)} className="rounded-sm h-8 w-32" data-testid={`leasing-iv-manu-${row.vin_prefix}`} placeholder="Hersteller" /></td>
                                            <td className="py-2 pr-3"><Input value={model} onChange={(e) => setEdit(row.vin_prefix, "model", e.target.value)} className="rounded-sm h-8 w-40" data-testid={`leasing-iv-model-${row.vin_prefix}`} placeholder="Modell" /></td>
                                            <td className="py-2 pr-3">
                                                <select value={fuel} onChange={(e) => setEdit(row.vin_prefix, "fuel", e.target.value)} className="border border-gray-300 rounded-sm h-8 w-24 text-sm bg-white" data-testid={`leasing-iv-fuel-${row.vin_prefix}`}>
                                                    <option value="">—</option>
                                                    <option value="Benzin">Benzin</option>
                                                    <option value="Elektro">Elektro</option>
                                                </select>
                                            </td>
                                            <td className="py-2 pr-3">
                                                <input list="leasing-ccm-opts" value={ccm} onChange={(e) => setEdit(row.vin_prefix, "displacement", e.target.value)} className="border border-gray-300 rounded-sm font-mono-num h-8 w-20 px-2 text-sm" data-testid={`leasing-iv-ccm-${row.vin_prefix}`} placeholder="ccm" />
                                            </td>
                                            <td className="py-2 pr-3 text-right">
                                                <select value={AUFSCHLAG_OPTS.includes(surch) || surch === "" ? surch : "__other"} onChange={(e) => setEdit(row.vin_prefix, "surcharge", e.target.value === "__other" ? surch : e.target.value)} className="border border-gray-300 rounded-sm font-mono-num h-8 w-28 ml-auto text-right text-sm bg-white" data-testid={`leasing-iv-input-${row.vin_prefix}`}>
                                                    <option value="">—</option>
                                                    {AUFSCHLAG_OPTS.map((o) => <option key={o} value={o}>{fmtEur(o)}</option>)}
                                                    {surch !== "" && !AUFSCHLAG_OPTS.includes(surch) && <option value="__other">{fmtEur(surch)}</option>}
                                                </select>
                                            </td>
                                            <td className="py-2 text-right whitespace-nowrap">
                                                <Button onClick={() => saveSurcharge(row)} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs" data-testid={`leasing-iv-save-${row.vin_prefix}`}>
                                                    <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                                                </Button>
                                                {row.rule_id && (
                                                    <button onClick={() => deleteRule(row)} className="ml-2 text-red-500 hover:text-red-700 align-middle" data-testid={`leasing-iv-del-${row.vin_prefix}`} title="Aufschlag/Regel löschen"><Trash2 className="w-4 h-4" /></button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {meta.pages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-3" data-testid="leasing-iv-pagination">
                        <button disabled={page <= 1} onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p); }} className="text-sm font-bold border border-gray-300 rounded-sm px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50" data-testid="leasing-iv-prev">Zurück</button>
                        <span className="text-sm text-gray-600">Seite {page} / {meta.pages}</span>
                        <button disabled={page >= meta.pages} onClick={() => { const p = Math.min(meta.pages, page + 1); setPage(p); load(p); }} className="text-sm font-bold border border-gray-300 rounded-sm px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50" data-testid="leasing-iv-next">Weiter</button>
                    </div>
                )}
            </div>
        </section>
    );
}


// Jun-2026 (User-Wunsch) — Rückgabe-E-Mail der Leasinggesellschaft inline pflegen.
// Diese Adresse nutzt „Fahrzeug sicherstellen" für die Mail an den Leasinggeber.
function CompanyEmailEdit({ company, onChanged }) {
    const [val, setVal] = useState(company.return_email || "");
    const [saving, setSaving] = useState(false);
    const dirty = (val || "") !== (company.return_email || "");
    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/dealer/leasing/companies/${company.id}`, {
                name: company.name, value_basis: company.value_basis,
                default_contract_type: company.default_contract_type || null,
                return_email: val.trim() || null,
            });
            toast.success("Rückgabe-E-Mail gespeichert");
            onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    return (
        <div className="mt-2 flex items-center gap-2" data-testid={`leasing-company-email-edit-${company.id}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 shrink-0">Rückgabe-E-Mail</span>
            <Input type="email" value={val} onChange={(e) => setVal(e.target.value)} placeholder="rueckgabe@leasing.de"
                className="rounded-sm h-8 text-sm max-w-[280px]" data-testid={`leasing-company-email-input-${company.id}`} />
            {dirty && (
                <Button onClick={save} disabled={saving} className="rounded-sm h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`leasing-company-email-save-${company.id}`}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                </Button>
            )}
        </div>
    );
}


function CompaniesCard({ companies, onChanged, busy }) {
    const [form, setForm] = useState({ name: "", value_basis: "gross", default_contract_type: "", return_email: "" });
    const [saving, setSaving] = useState(false);
    const add = async () => {
        if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
        setSaving(true);
        try {
            await api.post("/dealer/leasing/companies", { ...form, default_contract_type: form.default_contract_type || null });
            toast.success("Leasinggesellschaft angelegt");
            setForm({ name: "", value_basis: "gross", default_contract_type: "", return_email: "" });
            onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setSaving(false); }
    };
    const del = async (id) => {
        try { await api.delete(`/dealer/leasing/companies/${id}`); onChanged(); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };
    return (
        <section className="border rounded-sm bg-white" data-testid="leasing-companies-card">
            <div className="px-4 py-3 border-b flex items-center gap-2">
                <Building2 className="w-4 h-4 text-indigo-600" />
                <h3 className="font-black text-sm text-[#111827]">Leasinggesellschaften</h3>
            </div>
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name</label>
                        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="z. B. Santander Leasing" className="rounded-sm mt-1" data-testid="leasing-company-name" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Ablösewert</label>
                        <select value={form.value_basis} onChange={(e) => setForm((f) => ({ ...f, value_basis: e.target.value }))} className="w-full h-9 rounded-sm border border-gray-300 px-2 text-sm bg-white mt-1" data-testid="leasing-company-basis">
                            <option value="gross">brutto</option>
                            <option value="net">netto</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Vertragsart</label>
                        <select value={form.default_contract_type} onChange={(e) => setForm((f) => ({ ...f, default_contract_type: e.target.value }))} className="w-full h-9 rounded-sm border border-gray-300 px-2 text-sm bg-white mt-1" data-testid="leasing-company-contract">
                            <option value="">—</option>
                            <option value="RW">RW (Restwert)</option>
                            <option value="KM">KM (Kilometer)</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Rückgabe-E-Mail</label>
                        <Input type="email" value={form.return_email} onChange={(e) => setForm((f) => ({ ...f, return_email: e.target.value }))} placeholder="rueckgabe@leasing.de" className="rounded-sm mt-1" data-testid="leasing-company-email" />
                    </div>
                    <Button onClick={add} disabled={saving} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="leasing-company-add">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Anlegen
                    </Button>
                </div>
                {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : companies.length === 0 ? (
                    <div className="text-sm text-gray-400 py-3" data-testid="leasing-companies-empty">Noch keine Leasinggesellschaften.</div>
                ) : (
                    <div className="divide-y border rounded-sm" data-testid="leasing-companies-list">
                        {companies.map((c) => (
                            <div key={c.id} className="px-3 py-2" data-testid={`leasing-company-${c.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-sm">
                                        <span className="font-bold">{c.name}</span>
                                        <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${c.value_basis === "net" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>{c.value_basis === "net" ? "netto" : "brutto"}</span>
                                        {c.default_contract_type && <span className="ml-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-700">{c.default_contract_type}</span>}
                                    </div>
                                    <button onClick={() => del(c.id)} className="text-red-500 hover:text-red-700" data-testid={`leasing-company-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <CompanyEmailEdit company={c} onChanged={onChanged} />
                                <CompanyForms company={c} onChanged={onChanged} />
                                <BankLogoUpload company={c} onChanged={onChanged} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );

function BankLogoUpload({ company, onChanged }) {
    const [busy, setBusy] = useState(false);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        const fd = new FormData(); fd.append("file", file);
        try {
            await api.post(`/dealer/leasing/companies/${company.id}/bank-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Banklogo hochgeladen");
            onChanged();
        } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally { setBusy(false); e.target.value = ""; }
    };
    return (
        <div className="mt-2 flex items-center gap-3" data-testid={`leasing-banklogo-${company.id}`}>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Banklogo</span>
            {company.bank_logo_url
                ? <img src={`${BACKEND}${company.bank_logo_url}`} alt="Banklogo" className="h-7 w-auto border rounded-sm bg-white px-1" />
                : <span className="text-[11px] text-gray-400">kein Logo</span>}
            <label className="text-[11px] font-bold text-indigo-700 border border-indigo-200 rounded-sm px-2 py-1 cursor-pointer hover:bg-indigo-50 inline-flex items-center gap-1" data-testid={`leasing-banklogo-upload-${company.id}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {company.bank_logo_url ? "Ersetzen" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={onPick} />
            </label>
        </div>
    );
}

}

function CompanyForms({ company, onChanged }) {
    const [uploading, setUploading] = useState("");
    const forms = company.forms || [];
    const KINDS = [
        { kind: "rueckgabeprotokoll", label: "Rückgabeprotokoll" },
        { kind: "einverstaendnis", label: "Einverständniserklärung" },
        { kind: "other", label: "Weiteres Formular" },
    ];
    const upload = async (e, kind) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(kind);
        const fd = new FormData();
        fd.append("file", file); fd.append("kind", kind);
        try {
            await api.post(`/dealer/leasing/companies/${company.id}/forms`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Formular hochgeladen.");
            onChanged();
        } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen."); }
        finally { setUploading(""); e.target.value = ""; }
    };
    const del = async (fid) => {
        try { await api.delete(`/dealer/leasing/companies/${company.id}/forms/${fid}`); onChanged(); }
        catch { toast.error("Löschen fehlgeschlagen."); }
    };
    const byKind = (k) => forms.find((f) => f.kind === k);
    return (
        <div className="mt-2 pl-1 border-l-2 border-gray-100" data-testid={`leasing-company-forms-${company.id}`}>
            <div className="text-[10px] uppercase font-bold text-gray-400 mb-1 ml-2">Formular-Vorlagen</div>
            <div className="flex flex-wrap gap-2 ml-2">
                {KINDS.map(({ kind, label }) => {
                    const existing = kind !== "other" ? byKind(kind) : null;
                    return (
                        <label key={kind} className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-sm border cursor-pointer ${existing ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`leasing-form-upload-${kind}-${company.id}`}>
                            {uploading === kind ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : existing ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                            {label}{existing ? " ✓" : ""}
                            <input type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e, kind)} />
                        </label>
                    );
                })}
            </div>
            {forms.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 ml-2" data-testid={`leasing-company-forms-list-${company.id}`}>
                    {forms.map((f) => (
                        <span key={f.id} className="text-[11px] inline-flex items-center gap-1 bg-gray-50 border rounded-sm px-2 py-1">
                            <FileText className="w-3 h-3 text-gray-500" />
                            <a href={`${BACKEND}${f.url}`} target="_blank" rel="noreferrer" className="hover:underline">{f.filename || f.kind}</a>
                            <button onClick={() => del(f.id)} className="text-red-400 hover:text-red-600 ml-1" data-testid={`leasing-form-del-${f.id}`}><Trash2 className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function VinDecoder({ onLearned }) {
    const [vin, setVin] = useState("");
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const [learn, setLearn] = useState({ manufacturer: "", model: "", surcharge: "" });
    const decode = async () => {
        if (!vin.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.get("/dealer/leasing/decode-vin", { params: { vin } });
            setRes(data);
            setLearn((l) => ({
                manufacturer: data.matched_rule?.manufacturer || l.manufacturer,
                model: data.matched_rule?.model || l.model,
                surcharge: data.matched_rule?.surcharge != null ? String(data.matched_rule.surcharge) : l.surcharge,
            }));
        } catch { toast.error("FIN-Decode fehlgeschlagen"); }
        finally { setBusy(false); }
    };
    const saveLearn = async () => {
        if (!res?.decoded?.vin_prefix || !learn.manufacturer.trim()) { toast.error("Hersteller erforderlich"); return; }
        try {
            await api.post("/dealer/leasing/surcharge-rules", {
                manufacturer: learn.manufacturer, vin_prefix: res.decoded.vin_prefix,
                model: learn.model, surcharge: Number(learn.surcharge) || 0,
            });
            toast.success("Regel gelernt — Modell/Aufschlag gespeichert");
            onLearned();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    };
    const d = res?.decoded;
    return (
        <section className="border rounded-sm bg-white" data-testid="leasing-vin-decoder">
            <div className="px-4 py-3 border-b flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-indigo-600" />
                <h3 className="font-black text-sm text-[#111827]">FIN-Decoder & Anlernen (Modell aus FIN erkennen)</h3>
            </div>
            <div className="p-4 space-y-3">
                <div className="flex gap-2">
                    <Input value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} placeholder="Fahrgestellnummer (17 Zeichen)" className="rounded-sm font-mono-num uppercase" data-testid="leasing-vin-input" />
                    <VinWarning value={vin} />
                    <Button onClick={decode} disabled={busy} className="rounded-sm bg-[#111827] hover:bg-black text-white shrink-0" data-testid="leasing-vin-decode">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decodieren"}
                    </Button>
                </div>
                {d && (
                    <div className="text-xs bg-gray-50 border rounded-sm p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="leasing-vin-result">
                        <div><span className="text-gray-500">WMI:</span> <span className="font-mono-num font-bold">{d.wmi || "—"}</span></div>
                        <div><span className="text-gray-500">VDS:</span> <span className="font-mono-num font-bold">{d.vds || "—"}</span></div>
                        <div><span className="text-gray-500">Modelljahr:</span> <span className="font-bold">{d.model_year || "—"}</span></div>
                        <div><span className="text-gray-500">Präfix-10:</span> <span className="font-mono-num font-bold">{d.vin_prefix || "—"}</span></div>
                    </div>
                )}
                {d && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border-t pt-3">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Hersteller</label>
                            <Input value={learn.manufacturer} onChange={(e) => setLearn((l) => ({ ...l, manufacturer: e.target.value }))} className="rounded-sm mt-1" data-testid="leasing-learn-manufacturer" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Modell</label>
                            <Input value={learn.model} onChange={(e) => setLearn((l) => ({ ...l, model: e.target.value }))} className="rounded-sm mt-1" data-testid="leasing-learn-model" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Aufschlag (€)</label>
                            <Input type="number" value={learn.surcharge} onChange={(e) => setLearn((l) => ({ ...l, surcharge: e.target.value }))} className="rounded-sm mt-1 font-mono-num" data-testid="leasing-learn-surcharge" />
                        </div>
                        <Button onClick={saveLearn} className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white h-9" data-testid="leasing-learn-save">
                            <Save className="w-4 h-4 mr-1" /> Anlernen
                        </Button>
                    </div>
                )}
                {res && res.matched_rule && (
                    <div className="text-xs text-emerald-700" data-testid="leasing-vin-matched">Treffer gespeichert: {res.matched_rule.model || "—"} · Aufschlag {eur(res.matched_rule.surcharge)}</div>
                )}
            </div>
        </section>
    );
}
